import React, { ReactNode } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import isEqual from 'lodash-es/isEqual';
import { FormattedMessage } from 'react-intl';

import { DocNode } from '@atlaskit/adf-schema';
import { doc, mention, p } from '@atlaskit/adf-utils/builders';
import { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AkAvatar from '@atlaskit/avatar';
import AkComment, {
  CommentAction as AkCommentAction,
  CommentAuthor,
  CommentTime,
} from '@atlaskit/comment';
import {
  ProviderFactory,
  WithProviders,
} from '@atlaskit/editor-common/provider-factory';
import { NextEditorPlugin } from '@atlaskit/editor-common/types';
import { ExtensionProvidersProp } from '@atlaskit/editor-core/editor';
import Lozenge from '@atlaskit/lozenge';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Stack } from '@atlaskit/primitives';
import { ConnectedReactionsView } from '@atlaskit/reactions';
import { ReactRenderer } from '@atlaskit/renderer';
import { RelativeDate } from '@atlassian/bitkit-date';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import {
  ApiCommentOrPlaceholder,
  FabricComment,
  isApiComment,
  ApiToFabricCommentProps,
} from 'src/components/conversation-provider/types';
import { apiToFabricComment } from 'src/components/conversation-provider/utils';
import {
  apiCommentId,
  toConversationId,
} from 'src/components/conversation-provider/utils/to-conversation';
import { Editor } from 'src/components/editor/Editor';
import {
  rendererTTRSeverityTracking,
  unsupportedContentLevelsTracking,
} from 'src/constants/renderer-tracking';
import { ConversationsContextInterface } from 'src/contexts/conversations-context';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';

import { HttpError } from '../api/HttpError';
import CommentContainer from '../containers/Comment';
import {
  actionSubjectIds,
  eventTypes,
  fireEvent,
  trackEventActions,
} from '../internal/analytics';
import { getCurrentUserRole } from '../utils/get-current-user-role';

import { commentsInThread, CommentThread } from './comment-tree';
import messages from './comment.i18n';
import DeleteCommentModal from './delete-comment-modal';
import * as styles from './styles';
import {
  ConversationResolution,
  SendAnalyticsEvent,
  SuccessHandler,
} from './types';
// These are functions that can be passed to dispatch(), which is not the redux
// function, but rather a helper function specific to this component. We track
// the last dispatch as a string, so we list the dispatchable actions here to
// allow us to use these strings in the type system. If we don't do something
// along these lines, these props will incorrectly show up as being unused.
type DispatchActions = {
  onResolveComment?: (
    commentId: number,
    conversationResolution: ConversationResolution,
    onSuccess?: SuccessHandler
  ) => void;
  onAddComment?: (
    conversationId: string,
    parentId: number | null,
    value: any,
    pending?: boolean,
    onSuccess?: SuccessHandler
  ) => void;
  onDeleteComment?: (commentId: number, onSuccess?: SuccessHandler) => void;
  onEditorChange?: (
    isLocal: boolean,
    value: any,
    conversationId: string,
    commentId: number | undefined,
    meta: any,
    objectId: string,
    containerId?: string
  ) => void;
  onReopenComment?: (
    commentId: number,
    conversationResolution: ConversationResolution,
    onSuccess?: SuccessHandler
  ) => void;
  onRevertComment?: (conversationId: string, commentId: number) => void;
  onUpdateComment?: (
    commentId: number,
    value: any,
    onSuccess?: SuccessHandler
  ) => void;
};

export type Props = DispatchActions & {
  allowHelpButton?: boolean;
  canResolveThread?: boolean;
  containerId?: string;
  conversationsContext: ConversationsContextInterface;
  dataProviders?: ProviderFactory;
  disableScrollTo?: boolean;
  isHighlighted?: boolean;
  maxCommentNesting?: number;
  objectId: string;
  onCancel?: () => void;
  onEditorClose?: () => void;
  onEditorOpen?: () => void;
  onHighlightComment?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    commentId: number
  ) => void;
  onRetry?: (localId?: string) => void;
  onUserClick?: (user: FabricUser) => void;
  popupsMountPoint?: HTMLElement | null;
  portal?: HTMLElement;
  renderAdditionalCommentActions?: (
    CommentAction: typeof AkCommentAction,
    comment: FabricComment
  ) => JSX.Element[];
  renderAfterComment?: (comment: FabricComment) => JSX.Element;
  renderAuthorAvatar?: (user?: FabricUser) => ReactNode;
  renderAuthorName?: (user?: FabricUser) => ReactNode;
  renderCommentWithReplyMention?: boolean;
  setConversationResolution?: ConversationResolution;
  showDeleteCommentDialog?: boolean;
  user?: FabricUser;

  conversationId: string;
  canModerateComment?: boolean;
  comment: FabricComment;
  commentAuthorAccountId?: string;
  showBeforeUnloadWarning?: boolean;
  sendAnalyticsEvent: SendAnalyticsEvent;
  commentThread: CommentThread;

  nestedDepth: number;
  collapseThread?: () => void;
  extensionProviders?: ExtensionProvidersProp;
  codeSuggestionsExtensionHandler?: (
    commentId: number,
    apiComment: ApiCommentOrPlaceholder | undefined
  ) => any;
  editorPlugins?: [NextEditorPlugin<any>, boolean][];
  getSuggestions?: () => Record<string, string>;
};

export interface State {
  isEditing?: boolean;
  isReplying?: boolean;
  isDeleting: boolean;
  lastDispatch?: {
    handler: any;
    args: any[];
  };
}

/**
 * This functionality duplicates the mentionForCommentAuthor functionality
 * in src/components/bbc-atlaskit-editor/src/conversation-editor.tsx
 * with the exception of not needing to look for the correct author id
 * because we already found it.
 * in https://softwareteams.atlassian.net/browse/COREX-7485
 * @param comment
 * @param commentAuthorAccountId
 * @returns
 */
export const mentionForCommentAuthor = (
  comment: FabricComment,
  commentAuthorAccountId?: string | number
): DocNode | undefined => {
  let adfMention: DocNode | undefined = undefined;
  if (comment && comment.createdBy.id) {
    if (commentAuthorAccountId) {
      adfMention = doc(
        p(
          mention({
            id: `{${commentAuthorAccountId}}`,
            text: comment.createdBy.name,
            userType: 'DEFAULT',
          }),
          ' '
        )
      );
    }
  }
  return adfMention;
};

const apiCommentChanged = (
  oldComment: ApiCommentOrPlaceholder,
  newComment: ApiCommentOrPlaceholder
) => {
  if (oldComment.state !== newComment.state) {
    return true;
  }
  if (
    isApiComment(oldComment) &&
    isApiComment(newComment) &&
    oldComment.deleted !== newComment.deleted
  ) {
    return true;
  }
  if (isApiComment(oldComment) !== isApiComment(newComment)) {
    return true;
  }
  return false;
};

const commentChanged = (
  oldComment: FabricComment,
  newComment: FabricComment
) => {
  if (oldComment.state !== newComment.state) {
    return true;
  }

  if (oldComment.deleted !== newComment.deleted) {
    return true;
  }

  return false;
};

const userChanged = (
  oldUser: FabricUser = { id: '' },
  newUser: FabricUser = { id: '' }
) => {
  return oldUser.id !== newUser.id;
};

const Reactions = styled.div({
  height: '20px',
  '& > div': {
    height: '20px',
  },
});

export class Comment extends React.Component<Props, State> {
  deleteCommentAnalytics?: UIAnalyticsEvent;

  declare context: any;

  constructor(props: Props) {
    super(props);

    const { comment, renderAdditionalCommentActions } = this.props;

    this.state = {
      isEditing: false,
      isDeleting: false,
    };
    this.hideDeleteCommentModal = this.hideDeleteCommentModal.bind(this);
    this.showDeleteCommentModal = this.showDeleteCommentModal.bind(this);

    if (typeof renderAdditionalCommentActions === 'function') {
      this.additionalCommentActions =
        renderAdditionalCommentActions(AkCommentAction, comment) || [];
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { isEditing, isReplying, isDeleting } = this.state;
    const { isHighlighted, portal, comment, maxCommentNesting } = this.props;

    let editorChanged = false;
    if (nextProps.maxCommentNesting !== maxCommentNesting) {
      editorChanged = true;
    }

    let additionalCommentActions: JSX.Element[] = [];
    let additionalCommentsChanged = false;
    if (typeof nextProps.renderAdditionalCommentActions === 'function') {
      additionalCommentActions =
        nextProps.renderAdditionalCommentActions(
          AkCommentAction,
          nextProps.comment || comment
        ) || [];
    }

    if (!isEqual(additionalCommentActions, this.additionalCommentActions)) {
      this.additionalCommentActions = additionalCommentActions;
      additionalCommentsChanged = true;
    }

    if (
      nextState.isEditing !== isEditing ||
      nextState.isReplying !== isReplying ||
      nextState.isDeleting !== isDeleting ||
      nextProps.isHighlighted !== isHighlighted ||
      nextProps.portal !== portal ||
      editorChanged ||
      additionalCommentsChanged
    ) {
      return true;
    }

    if (commentChanged(this.props.comment, nextProps.comment)) {
      return true;
    }

    if (
      userChanged(
        this.props.conversationsContext.user,
        nextProps.conversationsContext.user
      )
    ) {
      return true;
    }

    const oldComments = commentsInThread(this.props.commentThread);
    const newComments = commentsInThread(nextProps.commentThread);

    if (oldComments.length !== newComments.length) {
      return true;
    }

    if (
      newComments.some(newComment => {
        const [matchingOldComment] = oldComments.filter(
          oldComment => apiCommentId(oldComment) === apiCommentId(newComment)
        );
        const isBrandNewComment = !matchingOldComment;
        return (
          isBrandNewComment || apiCommentChanged(matchingOldComment, newComment)
        );
      })
    ) {
      return true;
    }

    return false;
  }
  static contextType = DiffFeaturesContext;

  hideDeleteCommentModal() {
    this.setState({ isDeleting: false });
  }

  showDeleteCommentModal(_value: any, analyticsEvent?: UIAnalyticsEvent) {
    this.deleteCommentAnalytics = analyticsEvent;
    this.setState({ isDeleting: true });
  }

  private additionalCommentActions: JSX.Element[] = [];

  private dispatch = (dispatch: keyof DispatchActions, ...args: any[]) => {
    const handler = this.props[dispatch];

    if (handler) {
      handler.apply(handler, args);

      this.setState({
        lastDispatch: { handler: dispatch, args },
      });
    }
  };

  private onReply = (_event: any, analyticsEvent?: UIAnalyticsEvent) => {
    const {
      objectId,
      containerId,
      comment,
      conversationsContext: {
        activityFeedVersion,
        additionalAnalyticsAttributes,
      },
    } = this.props;

    const additionalAttributes = additionalAnalyticsAttributes
      ? additionalAnalyticsAttributes(comment, 'onReply')
      : {};

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.replyButton,
        objectId,
        containerId,
        attributes: {
          activityFeedVersion,
          ...additionalAttributes,
        },
      });
    }

    this.setState({
      isReplying: true,
    });
  };

  private onResolve = (_event: any, analyticsEvent?: UIAnalyticsEvent) => {
    const {
      comment,
      objectId,
      containerId,
      sendAnalyticsEvent,
      setConversationResolution,
      conversationsContext: {
        pullRequest,
        user,
        repoId,
        workspacePaid,
        activityFeedVersion,
        additionalAnalyticsAttributes,
      },
    } = this.props;

    const additionalAttributes = additionalAnalyticsAttributes
      ? additionalAnalyticsAttributes(comment, 'onResolve')
      : {};

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.resolveButton,
        objectId,
        containerId,
        attributes: {
          resolved: true,
          prStatus: pullRequest?.state,
          prId: pullRequest?.id,
          paid: workspacePaid,
          repoId,
          role: getCurrentUserRole(user, pullRequest),
          activityFeedVersion,
          ...additionalAttributes,
        },
      });
    }

    this.dispatch(
      'onResolveComment',
      comment.commentId,
      setConversationResolution,
      (id: string) => {
        sendAnalyticsEvent({
          actionSubjectId: id,
          action: trackEventActions.resolved,
          eventType: eventTypes.TRACK,
          actionSubject: 'comment',
          attributes: {
            nestedDepth: this.props.nestedDepth || 0,
          },
        });
      }
    );
  };

  private onReopen = (_event: any, analyticsEvent?: UIAnalyticsEvent) => {
    const {
      comment,
      objectId,
      containerId,
      sendAnalyticsEvent,
      setConversationResolution,
      conversationsContext: {
        pullRequest,
        user,
        repoId,
        workspacePaid,
        activityFeedVersion,
        additionalAnalyticsAttributes,
      },
    } = this.props;

    const additionalAttributes = additionalAnalyticsAttributes
      ? additionalAnalyticsAttributes(comment, 'onReopen')
      : {};

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.reopenButton,
        objectId,
        containerId,
        attributes: {
          resolved: false,
          prStatus: pullRequest?.state,
          prId: pullRequest?.id,
          paid: workspacePaid,
          repoId,
          role: getCurrentUserRole(user, pullRequest),
          activityFeedVersion,
          ...additionalAttributes,
        },
      });
    }

    this.dispatch(
      'onReopenComment',
      comment.commentId,
      setConversationResolution,
      (id: string) => {
        sendAnalyticsEvent({
          actionSubjectId: id,
          action: trackEventActions.reopened,
          eventType: eventTypes.TRACK,
          actionSubject: 'comment',
          attributes: {
            nestedDepth: this.props.nestedDepth || 0,
            activityFeedVersion,
          },
        });
      }
    );
  };

  private onSaveReply = (value: any, pending?: boolean) => {
    const {
      conversationId,
      comment: parentComment,
      sendAnalyticsEvent,
      conversationsContext: { activityFeedVersion, pullRequest },
    } = this.props;

    sendAnalyticsEvent({
      actionSubjectId: actionSubjectIds.saveButton,
    });

    this.dispatch(
      'onAddComment',
      conversationId,
      parentComment.commentId,
      value,
      pending,
      (id: string) => {
        sendAnalyticsEvent({
          actionSubjectId: id,
          action: trackEventActions.created,
          eventType: eventTypes.TRACK,
          actionSubject: 'comment',
          attributes: {
            nestedDepth: this.props.nestedDepth || 0,
            pending: !!parentComment.pending,
            activityFeedVersion,
            draft: pullRequest?.draft,
          },
        });
      }
    );

    this.setState({
      isReplying: false,
    });
  };

  private onCancelReply = () => {
    this.props.sendAnalyticsEvent({
      actionSubjectId: actionSubjectIds.cancelButton,
    });

    this.setState({
      isReplying: false,
    });
  };

  private onDelete = (_value: any, analyticsEvent?: UIAnalyticsEvent) => {
    const {
      comment,
      objectId,
      containerId,
      sendAnalyticsEvent,
      conversationsContext: {
        activityFeedVersion,
        additionalAnalyticsAttributes,
        pullRequest,
      },
    } = this.props;

    const additionalAttributes = additionalAnalyticsAttributes
      ? additionalAnalyticsAttributes(comment, 'onDelete')
      : {};

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.deleteButton,
        objectId,
        containerId,
        attributes: {
          activityFeedVersion,
          ...additionalAttributes,
        },
      });
    }

    this.dispatch('onDeleteComment', comment.commentId, (id: string) => {
      sendAnalyticsEvent({
        actionSubjectId: id,
        action: trackEventActions.deleted,
        eventType: eventTypes.TRACK,
        actionSubject: 'comment',
        attributes: {
          nestedDepth: this.props.nestedDepth || 0,
          pending: !!comment.pending,
          draft: pullRequest?.draft,
          activityFeedVersion,
        },
      });
    });
  };

  private onEdit = (_value: any, analyticsEvent?: UIAnalyticsEvent) => {
    const {
      objectId,
      containerId,
      conversationsContext: { activityFeedVersion },
    } = this.props;

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.editButton,
        objectId,
        containerId,
        attributes: {
          activityFeedVersion,
        },
      });
    }

    this.setState({
      isEditing: true,
    });
  };

  private getPopupsMountPoint = () => {
    // We default this to true because only global conversations disable this
    const { renderCommentWithReplyMention = true, popupsMountPoint } =
      this.props;
    return renderCommentWithReplyMention && popupsMountPoint
      ? popupsMountPoint
      : undefined;
  };

  private handleCommentEditorChange = (value: any) => {
    const { comment } = this.props;

    this.dispatch('onEditorChange', value, comment.commentId);
  };

  private onSaveEdit = (value: any) => {
    const {
      comment,
      sendAnalyticsEvent,
      conversationsContext: { activityFeedVersion, pullRequest },
    } = this.props;

    sendAnalyticsEvent({
      actionSubjectId: actionSubjectIds.saveButton,
    });

    this.dispatch('onUpdateComment', comment.commentId, value, (id: string) => {
      sendAnalyticsEvent({
        actionSubjectId: id,
        action: trackEventActions.updated,
        eventType: eventTypes.TRACK,
        actionSubject: 'comment',
        attributes: {
          nestedDepth: this.props.nestedDepth || 0,
          pending: !!comment.pending,
          activityFeedVersion,
          draft: pullRequest?.draft,
        },
      });
    });

    this.setState({
      isEditing: false,
    });
  };

  private onCancelEdit = () => {
    this.props.sendAnalyticsEvent({
      actionSubjectId: actionSubjectIds.cancelButton,
    });

    this.setState({
      isEditing: false,
    });
  };

  private onRequestCancel = (
    _value: any,
    analyticsEvent?: UIAnalyticsEvent
  ) => {
    const {
      comment,
      onCancel,
      objectId,
      containerId,
      conversationsContext: { activityFeedVersion },
    } = this.props;

    // Invoke optional onCancel hook
    if (onCancel) {
      onCancel();
    }

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.cancelFailedRequestButton,
        objectId,
        containerId,
        attributes: {
          activityFeedVersion,
        },
      });
    }

    this.dispatch('onRevertComment', comment.conversationId, comment.commentId);
  };

  private onRequestRetry = (_value: any, analyticsEvent?: UIAnalyticsEvent) => {
    const { lastDispatch } = this.state;
    const {
      objectId,
      containerId,
      onRetry,
      // We were previously trying to pull isPlaceholder from the comment, but
      // the types were wrong so it was never actually available. We're going
      // to hard-code isPlaceholder to false for now, but keep the code there
      // in case we want to try to restore this functionality.
      comment: { localId },
      conversationsContext: { activityFeedVersion },
    } = this.props;

    const isPlaceholder = false;
    if (onRetry && isPlaceholder) {
      onRetry(localId?.toString());
      return;
    }

    if (analyticsEvent) {
      fireEvent(analyticsEvent, {
        actionSubjectId: actionSubjectIds.retryFailedRequestButton,
        objectId,
        containerId,
        attributes: {
          activityFeedVersion,
        },
      });
    }

    if (!lastDispatch) {
      return;
    }

    this.dispatch(lastDispatch.handler, ...lastDispatch.args);
  };

  /**
   * Username click handler - pass a User object, returns a handler which will invoke onUserClick with it
   * @param {FabricUser} user
   */
  private handleUserClick = (user: FabricUser) => () => {
    const { onUserClick } = this.props;
    if (onUserClick && typeof onUserClick === 'function') {
      onUserClick(user);
    }
  };

  /**
   * This provides the functionality in useConversationEditorWithMentionReply
   * in src/components/bbc-atlaskit-editor/src/conversation-editor.tsx
   * to determine whether to put a reply mention as the default value in the
   * new comment reply. It does not need to return a default value if there is one because
   * the callback was used for both editing a comment and replying to a comment. Since this
   * is only used when replying, there will be no default value.
   * in https://softwareteams.atlassian.net/browse/COREX-7485
   * @returns string | undefined
   */
  private getReplyMention = () => {
    const {
      renderCommentWithReplyMention = true, // default to true because only global conversations don't do this
      commentAuthorAccountId,
      comment,
      conversationsContext: { currentUserUuid },
    } = this.props;

    if (
      renderCommentWithReplyMention &&
      comment &&
      comment.createdBy.id !== currentUserUuid &&
      this.props.nestedDepth !== 0
    ) {
      return mentionForCommentAuthor(comment, commentAuthorAccountId);
    }

    return undefined;
  };

  private getContent() {
    const {
      comment,
      commentThread,
      dataProviders,
      disableScrollTo,
      allowHelpButton,
      onEditorClose,
      onEditorOpen,
      portal,
      showBeforeUnloadWarning,
      objectId,
      sendAnalyticsEvent,
      conversationsContext: { user, renderBetweenCommentAndActions },
      collapseThread,
      codeSuggestionsExtensionHandler,
      extensionProviders,
      editorPlugins,
      getSuggestions,
    } = this.props;

    const { isEditing } = this.state;

    if (comment.deleted) {
      return <FormattedMessage tagName="em" {...messages.deletedComment} />;
    }

    const draftLocalStorageKey = `${objectId}-${comment.commentId}`;
    if (isEditing) {
      return (
        <Editor
          defaultValue={comment.document.adf}
          isExpanded
          isEditing={isEditing}
          onSave={this.onSaveEdit}
          onCancel={this.onCancelEdit}
          onClose={onEditorClose}
          onOpen={onEditorOpen}
          onChange={this.handleCommentEditorChange}
          user={user}
          disableScrollTo={disableScrollTo}
          allowHelpButton={allowHelpButton}
          showBeforeUnloadWarning={showBeforeUnloadWarning}
          draftLocalStorageKey={draftLocalStorageKey}
          popupsMountPoint={this.getPopupsMountPoint()}
          extensionProviders={extensionProviders}
          editorPlugins={editorPlugins}
          getSuggestions={getSuggestions}
        />
      );
    }

    if (comment.document.adf) {
      const apiComment: ApiCommentOrPlaceholder | undefined =
        commentThread.commentTree.get(comment.commentId);

      return (
        <Stack space="space.050">
          <ReactRenderer
            appearance="comment"
            analyticsEventSeverityTracking={rendererTTRSeverityTracking}
            unsupportedContentLevelsTracking={unsupportedContentLevelsTracking}
            useSpecBasedValidator
            // TypeScript seems to rightfully be pointing out that this might not
            // actually be a DocNode. Fixing other types exposed this error.
            // Since it's been working like this for a long time, we'll use a
            // type assertion to mask the warning, but it's not ideal.
            document={(comment.document.adf ?? {}) as DocNode}
            dataProviders={dataProviders}
            disableHeadingIDs
            portal={portal}
            allowCopyToClipboard
            extensionHandlers={codeSuggestionsExtensionHandler?.(
              comment.commentId,
              apiComment
            )}
          />
          {renderBetweenCommentAndActions &&
            renderBetweenCommentAndActions(
              comment,
              sendAnalyticsEvent,
              !commentThread.topComment.inline?.path,
              collapseThread
            )}
        </Stack>
      );
    }
    return null;
  }

  private getAfterContent() {
    const { renderAfterComment, comment } = this.props;

    return typeof renderAfterComment === 'function'
      ? renderAfterComment(comment)
      : null;
  }

  private renderComments() {
    const { comment, ...otherCommentProps } = this.props;
    // codeSuggestionsExtensionHandler is only available when the diff context has code suggestions
    // and the comment is an inline comment and the view mode ff is enabled
    const shouldParseCodeSuggestions = Boolean(
      otherCommentProps.codeSuggestionsExtensionHandler
    );
    const additionalParseOptions = { shouldParseCodeSuggestions };

    const thread = this.props.commentThread;
    const comments = thread.commentTree.childrenOf(comment.commentId).map(c => {
      const apiToFabricCommentProps: ApiToFabricCommentProps = {
        comment: c,
        conversationId: toConversationId(thread.topComment),
        additionalParseOptions,
      };
      return apiToFabricComment(apiToFabricCommentProps);
    });

    const { highlightCommentThread, highlightedCommentId } =
      otherCommentProps.conversationsContext;

    if (!comments || comments.length === 0) {
      return null;
    }

    return comments.map(child => {
      const isHighlighted =
        highlightedCommentId === child.commentId &&
        highlightCommentThread === false;
      return (
        <CommentContainer
          key={child.localId}
          comment={child}
          renderComment={props => (
            <Comment
              {...props}
              nestedDepth={this.props.nestedDepth + 1}
              isHighlighted={isHighlighted}
            />
          )}
          {...otherCommentProps}
        />
      );
    });
  }

  private renderEditor() {
    const { isReplying } = this.state;
    if (!isReplying) {
      return null;
    }

    const {
      disableScrollTo,
      allowHelpButton,
      onEditorClose,
      onEditorOpen,
      showBeforeUnloadWarning,
      comment,
      objectId,
      conversationsContext: { user },
      extensionProviders,
      editorPlugins,
      getSuggestions,
    } = this.props;

    const draftLocalStorageKey = `${objectId}-${comment.commentId}-new`;

    // true for pull request page only
    const { multiCommentReview } = this.context;

    return (
      <Editor
        isExpanded
        onCancel={this.onCancelReply}
        onSave={this.onSaveReply}
        onOpen={onEditorOpen}
        onClose={onEditorClose}
        onChange={this.handleCommentEditorChange}
        user={user}
        disableScrollTo={disableScrollTo}
        allowHelpButton={allowHelpButton}
        showBeforeUnloadWarning={showBeforeUnloadWarning}
        draftLocalStorageKey={draftLocalStorageKey}
        multiCommentReview={multiCommentReview}
        defaultValue={this.getReplyMention()}
        popupsMountPoint={this.getPopupsMountPoint()}
        extensionProviders={extensionProviders}
        editorPlugins={editorPlugins}
        getSuggestions={getSuggestions}
      />
    );
  }

  private getActions() {
    const {
      comment,
      dataProviders,
      objectId,
      canModerateComment,
      showDeleteCommentDialog,
      canResolveThread,
      conversationsContext: { user },
    } = this.props;
    const { isEditing } = this.state;
    const canReply = !!user && !isEditing && !comment.deleted;

    if (!canReply) {
      return undefined;
    }

    const isParent =
      this.props.commentThread.commentTree.childrenOf(
        this.props.comment.commentId
      ).length > 0;

    // This code was written as though comment weren't a FabricComment. The
    // types are fixed and even though we'll never actually get a commentAri
    // here the code here is useful, so we'll maintain the existing logic for
    // now.
    const { createdBy, commentAri } = comment as FabricComment & {
      commentAri: string;
    };

    let actions = [
      <AkCommentAction key="reply" onClick={this.onReply}>
        <FormattedMessage {...messages.replyLabel} />
      </AkCommentAction>,
    ];
    const resolveAction = (
      <AkCommentAction key="resolve" onClick={this.onResolve}>
        <FormattedMessage {...messages.resolveLabel} />
      </AkCommentAction>
    );
    const reopenAction = (
      <AkCommentAction key="reopen" onClick={this.onReopen}>
        <FormattedMessage {...messages.reopenLabel} />
      </AkCommentAction>
    );
    const editAction = (
      <AkCommentAction key="edit" onClick={this.onEdit}>
        <FormattedMessage {...messages.editLabel} />
      </AkCommentAction>
    );
    const deleteAction = (
      <AkCommentAction
        key="delete"
        onClick={
          showDeleteCommentDialog ? this.showDeleteCommentModal : this.onDelete
        }
      >
        <FormattedMessage {...messages.deleteLabel} />
      </AkCommentAction>
    );

    if (
      canResolveThread &&
      !comment.parentId &&
      !comment.resolution &&
      !comment.pending
    ) {
      actions = [...actions, resolveAction];
    } else if (canResolveThread && !comment.parentId && comment.resolution) {
      actions = [...actions, reopenAction];
    }

    if (createdBy && user && user.id === createdBy.id) {
      if (isParent) {
        actions = [...actions, editAction];
      } else {
        actions = [...actions, editAction, deleteAction];
      }
    } else if (user && canModerateComment && !isParent) {
      actions = [...actions, deleteAction];
    }

    if (this.additionalCommentActions && this.additionalCommentActions.length) {
      actions = [...actions, ...this.additionalCommentActions];
    }

    if (
      objectId &&
      commentAri &&
      dataProviders &&
      dataProviders.hasProvider('reactionsStore') &&
      dataProviders.hasProvider('emojiProvider')
    ) {
      actions = [
        ...actions,
        <WithProviders
          key="reactions"
          providers={['emojiProvider', 'reactionsStore']}
          providerFactory={dataProviders}
          renderNode={({ emojiProvider, reactionsStore }) => {
            if (
              typeof emojiProvider === 'undefined' ||
              typeof reactionsStore === 'undefined'
            ) {
              return null;
            }
            return (
              <Reactions>
                <ConnectedReactionsView
                  store={reactionsStore}
                  containerAri={objectId}
                  ari={commentAri}
                  emojiProvider={emojiProvider}
                />
              </Reactions>
            );
          }}
        />,
      ];
    }

    return actions;
  }

  private handleTimeClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const { comment, onHighlightComment, disableScrollTo } = this.props;

    if (!disableScrollTo && comment && onHighlightComment) {
      onHighlightComment(event, comment.commentId);
    }
  };

  private renderAuthor() {
    const { comment, onUserClick, renderAuthorName } = this.props;
    const { createdBy } = comment;

    if (renderAuthorName) {
      return renderAuthorName(createdBy);
    }

    return (
      <CommentAuthor
        onClick={onUserClick && this.handleUserClick(createdBy)}
        href={onUserClick ? '#' : createdBy.profileUrl}
      >
        {createdBy && createdBy.name}
      </CommentAuthor>
    );
  }

  private renderAvatar() {
    const { comment, renderAuthorAvatar } = this.props;
    const { createdBy } = comment;

    if (renderAuthorAvatar) {
      return renderAuthorAvatar(createdBy);
    }

    return (
      <AkAvatar
        src={createdBy && createdBy.avatarUrl}
        href={createdBy && createdBy.profileUrl}
        name={createdBy && createdBy.name}
      />
    );
  }

  private renderTimeOrPending() {
    const { comment, disableScrollTo } = this.props;

    if (comment.pending) {
      return (
        <Lozenge appearance="new">
          <FormattedMessage {...messages.pendingLozenge} />
        </Lozenge>
      );
    } else {
      const commentId = disableScrollTo
        ? undefined
        : `comment-${comment.commentId}`;
      return (
        <CommentTime
          onClick={this.handleTimeClick}
          href={disableScrollTo ? undefined : `#${commentId}`}
        >
          <RelativeDate date={new Date(comment.createdAt)} />
        </CommentTime>
      );
    }
  }

  render() {
    const { comment, isHighlighted, disableScrollTo, maxCommentNesting } =
      this.props;

    // We were previously trying to pull error from the comment, but the types
    // were wrong so it was never actually available. We're going to hard-code
    // error to undefined for now, but keep the code there in case we want to
    // try to restore this functionality.
    const { createdBy, state: commentState } = comment;
    const error = undefined as Error | undefined;
    const errorProps: {
      actions?: any[];
      message?: string;
    } = {};

    if (error) {
      errorProps.actions = [];

      if ((error as HttpError).canRetry) {
        errorProps.actions = [
          <AkCommentAction key="retry" onClick={this.onRequestRetry}>
            Retry
          </AkCommentAction>,
        ];
      }

      errorProps.actions = [
        ...errorProps.actions,
        <AkCommentAction key="cancel" onClick={this.onRequestCancel}>
          Cancel
        </AkCommentAction>,
      ];

      errorProps.message = error.message;
    }

    const comments = this.renderComments();
    const editor = this.renderEditor();
    const commentId = disableScrollTo
      ? undefined
      : `comment-${comment.commentId}`;

    return (
      <>
        <ModalTransition>
          {this.state.isDeleting && (
            <DeleteCommentModal
              analyticsEvent={this.deleteCommentAnalytics}
              onHideModal={this.hideDeleteCommentModal}
              onConfirmDelete={this.onDelete}
            />
          )}
        </ModalTransition>
        <AkComment
          id={commentId}
          author={this.renderAuthor()}
          avatar={this.renderAvatar()}
          type={createdBy && createdBy.type}
          time={this.renderTimeOrPending()}
          shouldRenderNestedCommentsInline={
            maxCommentNesting !== undefined &&
            this.props.nestedDepth !== undefined
              ? this.props.nestedDepth >= maxCommentNesting
              : false
          }
          actions={this.getActions()}
          content={this.getContent()}
          afterContent={this.getAfterContent()}
          isSaving={commentState === 'SAVING'}
          isError={commentState === 'ERROR'}
          errorActions={errorProps.actions}
          errorIconLabel={errorProps.message}
          highlighted={isHighlighted}
        >
          {editor || comments ? (
            <styles.CommentWrapper>
              {comments}
              {editor}
            </styles.CommentWrapper>
          ) : null}
        </AkComment>
      </>
    );
  }
}
